import { get, set, remove } from "js-cookie";
import { isEmpty } from "clientUtils";

/** CONSTANT */
import { AGENT_CONSTANTS } from '../modules/agents/constants/agent.constants';
const leadFormKey = 'LP_LEADFORM';

/**
* universal function to set cookie
* if not exist previously
* store on cookie in the browser for n days
* @param {String} key - key name to store on cookie
* @param {String} value - the value that will stored on cookie with it's key name
* @param {Number} expires - Define when the cookie will be removed
*/
export const setCookie = (key, value, expires) => {
  if (!get(key)) {
    set(key, value, { expires });
  } 
};

/**
* @function setLeadsToCookie set leads data into cookie if there is no LP_LEADFORM key previously
* @param {Object} data - leads data to store on cookie
*/
export const setLeadsToCookie = (data) => {
  const token = get('token');
  const organization = AGENT_CONSTANTS.getAgentByToken(token);

  if (organization.isInternalAgent && organization.name === 'Lifepal') {
    return;
  }

  const leadFormKey = 'LP_LEADFORM';
  const currentLeadsOnCookie = get(leadFormKey) && JSON.parse(get(leadFormKey));
  let leadsData = {};

  if (currentLeadsOnCookie && !isEmpty(currentLeadsOnCookie)) {
    leadsData = currentLeadsOnCookie;
    leadsData.phoneNumber = data.phone ? data.phone : currentLeadsOnCookie.phone;
    leadsData.gender = data.gender ? data.gender : currentLeadsOnCookie.gender;
    leadsData.age = data.age ? data.age : currentLeadsOnCookie.age;
    leadsData.fullName = data.fullName ? data.fullName : currentLeadsOnCookie.fullName;
  } else {
    leadsData.phoneNumber = data.phone;
    leadsData.gender = data.gender;
    leadsData.age = data.age;
    leadsData.fullName = data.fullName;
  }

  set(leadFormKey, JSON.stringify(leadsData), 30);
};

/**
* @function getLeadsFromCookie - retrieve cookie with key LP_LEADFORM to prefill leads data
* @return {Object} - returns object of leads data if any & empty object if none
*/
export const getLeadsFromCookie = () => {
  const token = get('token');
  const organization = AGENT_CONSTANTS.getAgentByToken(token);

  if (organization.isInternalAgent && organization.name === 'Lifepal') {
    return {};
  }

  try {
    let isKeyExist = get(leadFormKey);
    if (isKeyExist) return JSON.parse(get(leadFormKey))
    return {};
  } catch (error) {
    console.error(error);
    return {};
  }
};

/* MV "RESUME PREVIOUS SESSION" BANNER (lp_rebounce) - auto category only, shown on homepage
* (this repo); other eligible pages/repos should read+dismiss the same cookie, see docs. Key is
* always plain `lp_rebounce` (never env-suffixed) to match the documented cross-repo contract. */
const rebounceCookieKey = 'lp_rebounce';
// `hosts` are each env's exact hostnames (app + `-www` ingress alias, matched by equality since
// `-www` isn't a subdomain). `domain` lets sibling repos on the same env read the cookie too.
// develop/uat share `.lifepal.tech`, so `id` lets getRebounceCookie() reject a foreign-env cookie.
const REBOUNCE_ENVIRONMENTS = [
  { id: 'develop', hosts: ['azin.lifepal.tech', 'azin-www.lifepal.tech'], domain: '.lifepal.tech' },
  { id: 'uat', hosts: ['azin-uat.lifepal.tech', 'azin-www-uat.lifepal.tech'], domain: '.lifepal.tech' },
  { id: 'production', hosts: ['lifepal.co.id', 'www.lifepal.co.id'], domain: '.lifepal.co.id' },
];

// host-only (no `domain` attribute) everywhere else - localhost, preview/review apps, etc. - where
// a mismatched `domain` attribute would make the browser reject the cookie outright, and there is
// no cross-repo contract to honor anyway.
const getRebounceCookieDomain = () => {
  if (typeof window === 'undefined') return undefined;
  const { hostname } = window.location;
  const env = REBOUNCE_ENVIRONMENTS.find((entry) => entry.hosts.includes(hostname));
  return env?.domain;
};

// develop and uat share the `.lifepal.tech` cookie domain (see note above), so a cookie written on
// one is readable on the other; embed/compare this id in the payload so getRebounceCookie() can
// reject a foreign-environment cookie instead of resuming a session with a non-existent opportunity.
const getRebounceEnvId = () => {
  if (typeof window === 'undefined') return undefined;
  const { hostname } = window.location;
  return REBOUNCE_ENVIRONMENTS.find((entry) => entry.hosts.includes(hostname))?.id;
};

// e.g. review apps (`core-*.review.cx.lifepal.co.id`) aren't a listed `hosts` entry but still
// fall under the production `.lifepal.co.id` cookie domain, so they could still read a leaked
// production cookie even though getRebounceEnvId() returns undefined for them there.
const isHostWithinKnownCookieDomain = () => {
  if (typeof window === 'undefined') return false;
  const { hostname } = window.location;
  return REBOUNCE_ENVIRONMENTS.some((entry) => hostname === entry.domain.slice(1) || hostname.endsWith(entry.domain));
};

// js-cookie's set()/remove() 3rd arg needs an attributes object, not a raw number, or `expires`
// is silently dropped. `domain` must match on removal too. `secure` is only set on actual HTTPS
// deploys so local HTTP dev still works (see QualificationContainerSF.js:485-490).
const getRebounceCookieAttributes = () => {
  const domain = getRebounceCookieDomain();
  const isSecureContext = typeof window !== 'undefined' && window.location.protocol === 'https:';
  return {
    expires: 30,
    path: '/',
    ...(domain ? { domain } : {}),
    ...(isSecureContext ? { secure: true } : {}),
  };
};

// only allow-listed, primitive plan-resume params are ever assembled into the stored `url` (see
// containers/plan-listing/utils.js#buildRebounceUrl) - never arbitrary router query values.
export const REBOUNCE_PARAM_KEYS = [
  'category',
  'namePlan',
  'vehicleType',
  'vehicleTypeId',
  'year',
  'region',
  'area',
  'carPlateProvince',
  'opportunityNumber',
  'sfQuoteId',
  'qualificationId',
];

// matches the shared cookie's validation used by other repos (rebounce-on-asuransi.js#isSafeRelativeUrl)
// so a malformed/malicious `url` is never trusted as a redirect target. CodeQL
// (js/client-side-unvalidated-url-redirection): rejects protocol-relative, backslash, and scheme-prefixed values.
const isSafeRelativeUrl = (url) => {
  if (typeof url !== 'string' || url === '') return false;
  if (!/^\/(?!\/|\\)[^\s\\]*$/.test(url)) return false;
  return !/^[a-z][a-z\d+.-]*:/i.test(url.slice(1));
};

/**
* @function getRebounceCookie - retrieve the lp_rebounce cookie used by the MV "resume previous session" banner
* @return {Object} - { vehicle: String, url: String } or {} if none/invalid
*/
export const getRebounceCookie = () => {
  try {
    const raw = get(rebounceCookieKey);
    if (!raw) return {};
    const data = JSON.parse(raw);
    // validate shape before trusting a malformed/stale shared cookie: a non-string `vehicle`
    // would be rendered directly as a React child, and an unsafe `url` would be handed
    // straight to next/link as a redirect target.
    if (!data || typeof data !== 'object' || typeof data.vehicle !== 'string' || !isSafeRelativeUrl(data.url)) {
      return {};
    }

    // develop/uat share a cookie domain (see REBOUNCE_ENVIRONMENTS); reject any cookie not
    // stamped for the current environment - including pre-discriminator cookies with no
    // `env` at all - so a foreign opportunityNumber/sfQuoteId is never resumed here.
    const currentEnvId = getRebounceEnvId();
    if (currentEnvId && data.env !== currentEnvId) {
      return {};
    }
    // an ineligible host (e.g. review apps) can still sit within a known domain's scope and
    // read a cookie leaked from that environment, even though it has no env id of its own.
    if (!currentEnvId && isHostWithinKnownCookieDomain()) {
      return {};
    }

    return {
      vehicle: data.vehicle,
      url: data.url,
    };
  } catch (error) {
    console.error(error);
    return {};
  }
};

/**
* @function setRebounceCookie - store the dynamic vehicle text + the /plans resume url (see
* containers/plan-listing/utils.js#buildRebounceUrl) on /plans (auto category) so the banner can
* show on other eligible pages and send the customer back to the exact same listing.
* @param {String} vehicle - dynamic part of the banner copy, e.g. "Toyota Avanza MPV 2020"
* @param {String} url - allow-listed-params-only /plans path to redirect back to, e.g. "/plans?category=auto&namePlan=..."
*/
export const setRebounceCookie = (vehicle, url) => {
  if (!isSafeRelativeUrl(url)) return;
  set(
    rebounceCookieKey,
    JSON.stringify({ vehicle, url, env: getRebounceEnvId() }),
    getRebounceCookieAttributes()
  );
};

/**
* @function removeRebounceCookie - clear any previous vehicle text as soon as the customer lands on /plans
* (auto category), so stale data from a prior visit (different vehicle, etc.) never leaks into the
* new one. Call this on mount of /plans, before setRebounceCookie recomputes the fresh vehicle text.
*/
export const removeRebounceCookie = () => {
  remove(rebounceCookieKey, getRebounceCookieAttributes());
};



